import { createContext, type ReactNode,useCallback, useContext, useMemo } from 'react';

import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import type { CollectionDtoMetadata, CollectionParameterValue } from 'data/generated/graphql';

export type ParameterValue = string | boolean;

// The GraphQL JSON scalar is typed as Record<string, unknown>, but the actual
// values are primitives (string/boolean/enum). Cast at the boundary.
type StoredParameter = Omit<CollectionParameterValue, 'value'> & { value: ParameterValue };

type CollectionParametersContextValue = {
  values: Record<string, ParameterValue>;
  setValue: (key: string, next: ParameterValue) => void;
};

const CollectionParametersContext = createContext<CollectionParametersContextValue | null>(null);

export function CollectionParametersProvider({ children }: { children: ReactNode }) {
  const { metadata, setNewMetadata } = useDataModelContext();
  const persisted = (metadata as CollectionDtoMetadata).collectionParameters;

  const values = useMemo(
    () =>
      (((persisted ?? []) as unknown) as StoredParameter[]).reduce<Record<string, ParameterValue>>(
        (acc, { name, value }) => {
          acc[name] = value;
          return acc;
        },
        {}
      ),
    [persisted]
  );

  const setValue = useCallback(
    (key: string, next: ParameterValue) => {
      const existing = (((metadata as CollectionDtoMetadata).collectionParameters ?? []) as unknown) as StoredParameter[];
      const index = existing.findIndex((p) => p.name === key);
      const nextEntry: StoredParameter = { name: key, value: next };
      const nextParameters =
        index === -1
          ? [...existing, nextEntry]
          : existing.map((entry, i) => (i === index ? nextEntry : entry));
      setNewMetadata({
        collectionParameters: nextParameters as unknown as CollectionParameterValue[]
      } as Partial<CollectionDtoMetadata>);
    },
    [metadata, setNewMetadata]
  );

  const contextValue = useMemo(() => ({ values, setValue }), [values, setValue]);

  return (
    <CollectionParametersContext.Provider value={contextValue}>{children}</CollectionParametersContext.Provider>
  );
}

export function useCollectionParameter(key: string) {
  const ctx = useContext(CollectionParametersContext);
  if (!ctx) {
    throw new Error('useCollectionParameter must be used within CollectionParametersProvider');
  }
  return {
    value: ctx.values[key],
    setValue: (next: ParameterValue) => ctx.setValue(key, next)
  };
}
